import { useEffect, useRef, useState } from "react";

import { AnswerField } from "../ExerciseFields/AnswerField";
import { AnswerList } from "./AnswerList";
import { NoCorrectAnswerDialog } from "./NoCorrectAnswerDialog";
import { ModelMissingDialog } from "./ModelMissingDialog";
import { DescriptionField } from "../ExerciseFields/DescriptionField";
import { LanguageField } from "../ExerciseFields/LanguageField";

import { NameField } from "../ExerciseFields/NameField";
import { QuestionField } from "../ExerciseFields/QuestionField";
import { SchemaField } from "../ExerciseFields/SchemaField";
import { FeedUpField } from "../ExerciseFields/FeedUpField";
import { FeedForwardPosField } from "../ExerciseFields/FeedForwardPosField";
import { FeedForwardNegField } from "../ExerciseFields/FeedForwardNegField";
import { FeedForwardThresholdField } from "../ExerciseFields/FeedForwardThresholdField";
import { ScoreField } from "../ExerciseFields/ScoreField";
import { b64EncodeUnicode } from "../../../shared/b64";
import { emptyExercise } from "../../../types/Exercise";
import { useExerciseContext } from "../../../context/ExerciseContext";
import { useNavigate } from "react-router-dom";
import { useModelContext } from "../../../context/ModelContext";
import { useModelEditorModalContext } from "../../../context/ModelEditorModalContext";
import { useTranslation } from "react-i18next";
import Tooltip from "@kea-mod/common/tooltip";
import { ModelList } from "../../../pages/Modellist";
import { AddModelCard } from "../../Models/AddModelCard";

type Props = {
  assignmentId: any;
};

export const AddExerciseUnderstandingComponent = (props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const exerciseContext = useExerciseContext();
  const modelContext = useModelContext();
  const modelEditorModalContext = useModelEditorModalContext();

  const [activeExercise, setActiveExercise] = useState(emptyExercise);
  const [exerciseId, setExerciseId] = useState(-1);
  const [exerciseCreated, setExerciseCreated] = useState(false);
  // The state flag is only updated on the next render, so two quick clicks would both
  // pass the check and create the exercise twice. The running promise is shared instead.
  const creating = useRef<Promise<number> | null>(null);
  const createdId = useRef(-1);
  const [language, setLanguage] = useState("");
  const [answer, setAnswer] = useState("");
  const [isCorrect, setIsCorrect] = useState(false);
  const [maxScore, setMaxScore] = useState("0");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "failed">("idle");
  const [modelMissingOpen, setModelMissingOpen] = useState(false);
  const [noCorrectAnswerOpen, setNoCorrectAnswerOpen] = useState(false);
  // Answer options live in the form until the author saves; only then are they written.
  const [draftAnswers, setDraftAnswers] = useState<Array<any>>([]);
  const nextDraftAnswerId = useRef(1);

  useEffect(() => {
    exerciseContext.all();
    setDefaultAssessmentSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDefaultAssessmentSchema = () => {
    setActiveExercise({
      ...activeExercise,
      schemaId: "27", //id of Multiple Choice Assessment Schema
    });
  };

  const handleChange = (event: any) => {
    let value = event.target.value;

    setActiveExercise({
      ...activeExercise,
      [event.target.name]: value,
    });
  };

  const handleChangeQuestion = (event: any) => {
    let value = event.target.value;

    setActiveExercise({
      ...activeExercise,
      understanding: { ...activeExercise.understanding, question: value },
    });
  };

  const handleCheckboxChange = (event: any) => {
    setActiveExercise({
      ...activeExercise,
      [event.target.name]: event.target.checked,
    });
  };

  const handleChangeLanguage = (event: any) => {
    setLanguage(event.target.value);
  };

  const handleChangeDescription = (event: any) => {
    setActiveExercise({
      ...activeExercise,
      description: event,
    });
  };

  const handleChangeAnswer = (event: any) => {
    setAnswer(event.target.value);
  };

  const handleCheckAnswer = () => {
    setIsCorrect(!isCorrect);
  };

  const emptyCheck = () => {
    if (
      activeExercise.name.trim() === "" ||
      activeExercise.description.trim() === "" ||
      language === "" ||
      activeExercise.understanding.question.trim() === "" ||
      activeExercise.schemaId === ""
    ) {
      return true;
    } else {
      return false;
    }
  };

  const answers = () => {
    return draftAnswers;
  };

  /** Names the parts the author still has to provide, so a rejected save can say why. */
  const missingParts = () => {
    let missing: string[] = [];

    if (activeExercise.name.trim() === "") missing.push(t("name"));
    if (language === "") missing.push(t("modellinglang"));
    if (activeExercise.description.trim() === "") missing.push(t("description"));
    if (activeExercise.understanding.question.trim() === "") missing.push(t("question"));
    if (activeExercise.schemaId === "") missing.push(t("schema"));
    if (answers().length === 0) missing.push(t("answers"));

    return missing;
  };

  const addFeedFields = (data: any) => {
    //only add fields to data if not empty
    if (activeExercise.feedUp !== "") {
      data.feedUp = activeExercise.feedUp;
    }

    if (activeExercise.feedForward_pos !== "") {
      data.feedForward_pos = activeExercise.feedForward_pos;
    }

    if (activeExercise.feedForward_neg !== "") {
      data.feedForward_neg = activeExercise.feedForward_neg;
    }

    if (activeExercise.feedForward_threshold !== "") {
      data.feedForward_threshold = parseFloat(activeExercise.feedForward_threshold);
    }
  };

  const exerciseData = () => {
    let data: any = {
      name: activeExercise.name,
      type: "understanding",
      description: b64EncodeUnicode(activeExercise.description),
      language: language,
      question: activeExercise.understanding.question,
      schemaId: parseInt(activeExercise.schemaId),
      assignmentId: parseInt(props.assignmentId),
      directFeedback: activeExercise.directFeedback,
      maxTotalScore: maxScore,
    };

    addFeedFields(data);

    return data;
  };

  const createExercise = async () => {
    try {
      let createdId = await exerciseContext.create(exerciseData());
      return createdId === undefined ? -1 : createdId;
    } catch (e) {
      console.warn(e);
      alert(t("exerciseSaveFailed"));
      return -1;
    }
  };

  /**
   * The given model and the answer options are stored with a reference to their exercise, so the
   * exercise row has to exist before either can be added. Instead of asking the author to create
   * the exercise in a separate step, it is created here on demand and updated again on save.
   */
  const ensureExerciseExists = async () => {
    if (createdId.current !== -1) {
      return createdId.current;
    }

    if (creating.current !== null) {
      return creating.current;
    }

    if (emptyCheck()) {
      alert(t("completeBeforeAddingParts"));
      return -1;
    }

    creating.current = createExercise();
    let newId = -1;
    try {
      newId = await creating.current;
    } catch (error) {
      newId = -1;
    }
    creating.current = null;

    if (newId === -1) {
      return -1;
    }

    createdId.current = newId;
    setExerciseId(newId);
    setExerciseCreated(true);

    return newId;
  };

  const openInputModelEditor = async () => {
    let wasCreated = exerciseCreated;
    let id = await ensureExerciseExists();
    if (id === -1) return;

    // the answer list is shared context state, so it has to be tied to this exercise once it exists
    if (!wasCreated) exerciseContext.updateAllAnswers(id);

    modelEditorModalContext.setModelingLanguage(language);
    modelEditorModalContext.setType("input");
    modelEditorModalContext.setExerciseId(id.toString());
    modelEditorModalContext.toggleModal();
  };

  const goBackToAssignment = () => {
    navigate("/assignments/" + props.assignmentId);
  };

  const activeModels = () => {
    if (!exerciseCreated) return [];
    return modelContext.models.filter((model: any) => model.parentId === parseInt(exerciseId.toString()));
  };

  const showModelList = () => {
    const models = activeModels();

    return (
      <div className="field">
        <label className="label" htmlFor="title">
          {t("inputModel")}
          <Tooltip
            icon="circle-info"
            class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
            content={t("inputmodelTooltip")}
          ></Tooltip>
        </label>
        {/* Adding a model creates the exercise, which the rest of the form no longer does. */}
        <div className="notification is-info is-light py-3 px-4">{t("exerciseCreatedForModelNote")}</div>
        <ModelList
          modelList={models}
          modelingLanguage={language}
          exerciseId={exerciseId}
          exerciseType={"understanding"}
          modelType={"input"}
        />
      </div>
    );
  };

  let modelList = (
    <div>
      {exerciseCreated ? (
        showModelList()
      ) : (
        <div className="field">
          <label className="label" htmlFor="title">
            {t("inputModel")}
            <Tooltip
              icon="circle-info"
              class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
              content={t("inputmodelTooltip")}
            ></Tooltip>
          </label>
          <div className="section">
            <div className="columns is-multiline is-centered">
              <AddModelCard openModellingTool={openInputModelEditor} />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const addAnswer = () => {
    if (answer.trim() === "") {
      alert("Please make sure that the field answer is not empty!");
      return;
    }

    setDraftAnswers([...draftAnswers, { id: nextDraftAnswerId.current++, text: answer, isCorrect: isCorrect }]);
    setAnswer("");
    setIsCorrect(false);
  };

  const removeAnswer = (answerId: any) => {
    setDraftAnswers(draftAnswers.filter((entry: any) => entry.id !== answerId));
  };

  const editAnswer = (answer: any, changes: { text: string; isCorrect: boolean }) => {
    setDraftAnswers(
      draftAnswers.map((entry: any) => (entry.id === answer.id ? { ...entry, ...changes } : entry)),
    );
  };

  let answerList = (
    <div>
      <label className="label">{t("answers")}</label>
      <AnswerField
        answer={answer}
        isCorrect={isCorrect}
        handleCheckAnswer={handleCheckAnswer}
        handleChangeAnswer={handleChangeAnswer}
        handleAddAnswer={addAnswer}
      />

      <AnswerList answers={answers()} deleteAnswer={removeAnswer} saveAnswer={editAnswer} />
    </div>
  );

  const saveExercise = async () => {
    let missing = missingParts();
    if (missing.length > 0) {
      alert(t("completeBeforeSaving") + " " + missing.join(", "));
      return;
    }

    if (!answers().some((entry: any) => entry.isCorrect)) {
      setNoCorrectAnswerOpen(true);
      return;
    }

    if (activeModels().length === 0) {
      setModelMissingOpen(true);
      return;
    }

    await persistExercise();
  };

  const persistExercise = async () => {
    let data = {
      name: activeExercise.name,
      type: "understanding",
      description: b64EncodeUnicode(activeExercise.description),
      language: language,
      understanding: { question: activeExercise.understanding.question },
      schemaId: parseInt(activeExercise.schemaId),
      assignmentId: parseInt(props.assignmentId),
      maxTotalScore: maxScore,
    };

    // Awaited so that a failure stays on the form instead of navigating away as if it worked.
    setSaveState("saving");

    // The exercise exists at this point only if a model was added, because that needs one.
    // Otherwise it is created here, and the answer options follow in a single request so a
    // rejected write leaves none of them behind.
    let existed = exerciseCreated;
    let id = await ensureExerciseExists();
    if (id === -1) {
      setSaveState("failed");
      return;
    }

    try {
      if (existed) await exerciseContext.update(id, data);
      const written = await exerciseContext.createAnswers(id, {
        answer: draftAnswers.map((entry: any) => ({ text: entry.text, isCorrect: entry.isCorrect })),
      });
      if (written === false) {
        setSaveState("failed");
        return;
      }
    } catch (error) {
      setSaveState("failed");
      return;
    }
    setSaveState("idle");
    navigate("/assignments/" + props.assignmentId);
  };
  return (
    <div>
      {noCorrectAnswerOpen && (
        <NoCorrectAnswerDialog
          exerciseExists={exerciseCreated}
          onSaveAnyway={() => {
            setNoCorrectAnswerOpen(false);
            if (activeModels().length === 0) {
              setModelMissingOpen(true);
              return;
            }
            persistExercise();
          }}
          onBack={() => setNoCorrectAnswerOpen(false)}
        />
      )}
      {modelMissingOpen && (
        <ModelMissingDialog
          exerciseExists={exerciseCreated}
          onSaveAnyway={() => {
            setModelMissingOpen(false);
            persistExercise();
          }}
          onAddModel={() => {
            setModelMissingOpen(false);
            openInputModelEditor();
          }}
          onBack={() => setModelMissingOpen(false)}
        />
      )}
      <NameField activeExercise={activeExercise} h={handleChange} />
      <LanguageField language={language} h={handleChangeLanguage} />
      <DescriptionField activeExercise={activeExercise} h={handleChangeDescription} />
      <SchemaField activeExercise={activeExercise} h={handleChange} />
      <FeedUpField activeExercise={activeExercise} h={handleChange} />
      <FeedForwardPosField activeExercise={activeExercise} h={handleChange} />
      <FeedForwardNegField activeExercise={activeExercise} h={handleChange} />
      <FeedForwardThresholdField activeExercise={activeExercise} h={handleChange} />
      <div className="field">
        <div>
          <div className="label">
            {t("autoAssessmentEx")}
            <Tooltip
              icon="circle-info"
              class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
              content={t("autoAssessmentExDesc")}
            ></Tooltip>
          </div>
        </div>
        <input
          name="directFeedback"
          type="checkbox"
          checked={activeExercise.directFeedback}
          onChange={handleCheckboxChange}
        />
      </div>
      <ScoreField maxScore={maxScore} setMaxScore={setMaxScore} />
      <QuestionField activeExercise={activeExercise} h={handleChangeQuestion} />
      {modelList}
      {answerList}
      {saveState === "failed" && (
        <div className="notification is-danger is-light mt-4">{t("exerciseSaveFailed")}</div>
      )}
      <div className="columns mt-4">
        <button
          className={"button is-normal is-success" + (saveState === "saving" ? " is-loading" : "")}
          disabled={saveState === "saving"}
          onClick={saveExercise}
        >
          {t("saveExercise")}
        </button>
        <button className="button" disabled={saveState === "saving"} onClick={goBackToAssignment}>
          {t("backToAssignment")}
        </button>
      </div>
    </div>
  );
};
